import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button } from "reactstrap";
import {
  Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, SortableHeaderCell, EntityRow, ConfirmationModal,
  NotificationToast, SimpleFormModal, SearchBar, InformationalModal
} from "../components";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { electricalSystemsApi } from "../api/entitiesApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { electricalSystemFields, electricalSystemDefaultValues } from "../utils/entityFormFields";
import { Link } from "react-router-dom";

// styles
import "../App.css";

export function ElectricalSystemsComponent() {
  const { data: electricalSystems, isLoading, error, setData: setElectricalSystems, setError } = useEntity(electricalSystemsApi);
  const { items: sortedElectricalSystems, requestSort, sortConfig } = useSortableData(electricalSystems, { key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [electricalSystemsPerPage, setElectricalSystemsPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, electricalSystemId: null });
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });


  const totalPages = Math.ceil(electricalSystems.length / electricalSystemsPerPage);
  const indexOfLastElectricalSystem = currentPage * electricalSystemsPerPage;
  const indexOfFirstElectricalSystem = indexOfLastElectricalSystem - electricalSystemsPerPage;
  const itemsPerPage = [50, 100];
  const filteredElectricalSystems = sortedElectricalSystems.filter(electricalSystem => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      (electricalSystem.id && electricalSystem.id.toString().toLowerCase().includes(lowerTerm)) ||
      (electricalSystem.customer_id && electricalSystem.customer_id.toString().toLowerCase().includes(lowerTerm)) ||
      (electricalSystem.site_id && electricalSystem.site_id.toString().toLowerCase().includes(lowerTerm))
    );
  });
  const currentElectricalSystems = filteredElectricalSystems.slice(indexOfFirstElectricalSystem, indexOfLastElectricalSystem);
  function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
  function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
  function handleDropdownChange(number) {
    setElectricalSystemsPerPage(number);
    setCurrentPage(1);
  };

  function handleEdit(electricalSystem) {
    setCurrentForm({ mode: "edit", data: electricalSystem });
  };

  function handleAdd() {
    setCurrentForm({ mode: "add", data: { customer_id: "", site_id: "" } });
  }

  async function handleSaveForm(formData) {
    try {
      if (currentForm.mode === "edit") {
        await electricalSystemsApi.updateEntity(formData.id, formData);
        setElectricalSystems(prevData =>
          prevData.map(electricalSystem =>
            electricalSystem.id === formData.id ? formData : electricalSystem
          )
        );
        setNotification({ visible: true, title: "ElectricalSystem Edited", message: "1 ElectricalSystem has been edited" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      } else {
        const newElectricalSystem = await electricalSystemsApi.addEntity(formData);
        setElectricalSystems(prevData => [...prevData, newElectricalSystem]);
        setNotification({ visible: true, title: "ElectricalSystem Added", message: "1 ElectricalSystem has been added" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      }
    } catch (err) {
      setErrorMessage({ open: true, title: currentForm.mode === "edit" ? "Error Updating ElectricalSystem" : "Error Adding ElectricalSystem", message: err.message });
    } finally {
      setCurrentForm(null);
    }
  }

  function handleCancelFormModal() {
    setCurrentForm(null);
  }

  function openDeleteModal(electricalSystem) {
    setDeleteData({ open: true, electricalSystemId: electricalSystem.id });
  }

  async function confirmDelete() {
    try {
      await electricalSystemsApi.deleteEntity(deleteData.electricalSystemId);
      setElectricalSystems((prevElectricalSystems) =>
        prevElectricalSystems.filter((electricalSystem) => electricalSystem.id !== deleteData.electricalSystemId)
      );
      setNotification({ visible: true, title: "ElectricalSystem Deleted", message: "1 ElectricalSystem has been deleted" });
      setTimeout(() => {
        setNotification({ visible: false, title: "", message: "" });
      }, 2000);
    } catch (err) {
      setErrorMessage({ open: true, title: "Error Deleting ElectricalSystem", message: err.message })
    } finally {
      setDeleteData({ open: false, electricalSystemId: null });
    }
  }

  function cancelDelete() {
    setDeleteData({ open: false, electricalSystemId: null });
  };

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" })
  }

  if (isLoading) {
    return <Loading />;
  }

  const displayError = error
    ? error.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
    : "Ooops! No electricalSystems found";

  return (
    <>
      {electricalSystems.length === 0 && (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={displayError} />
          <br />
          <div className="d-flex justify-content-center">
            <Button
              color="primary"
              onClick={(e) => {
                e.currentTarget.blur();
                setError(null);
                handleAdd();
              }}
              aria-label={error ? "Create First ElectricalSystem" : "Create ElectricalSystem"}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {electricalSystems.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <div style={{ marginRight: "1rem" }}>
              <Button
                color="transparent"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleAdd();
                }}
                aria-label="Add ElectricalSystem"
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
            <div>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>
          </div>

          {filteredElectricalSystems.length > 0 ? (
            <>
              <Table bordered hover responsive size="sm" striped>
                <thead>
                  <tr>
                    <SortableHeaderCell columnKey={"customer_id"} label={"Customer_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"id"} label={"Id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"site_id"} label={"Site_id"} currentSort={sortConfig} onSort={requestSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentElectricalSystems.map((electricalSystem) => (
                    <EntityRow
                      key={electricalSystem.id}
                      entity={electricalSystem}
                      fields={["customer_id", "id", "site_id"]}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={
                        (currentForm && currentForm.mode === "edit" && electricalSystem.id === currentForm.data.id) ||
                        electricalSystem.id === deleteData.electricalSystemId
                      }
                      renderField={(field, value) => {
                        if (field === "customer_id") {
                          return <Link to={`/customerDetails/${value}`}>{value.substring(0, 4)}</Link>;
                        }
                        return value;
                      }}
                    />
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <ItemsPerPageSelector
                  items={itemsPerPage}
                  currentSelection={electricalSystemsPerPage}
                  onSelect={handleDropdownChange}
                  isOpen={dropdownOpen}
                  toggle={toggleDropdown}
                />
              </div>
            </>
          ) : (
            <WarningAlert message="Ooops! No electricalSystems found" />
          )}
        </div>
      )}

      <InformationalModal
        isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message}
        onCancel={dismissInformationalModal}
      />

      <ConfirmationModal
        isOpen={deleteData.open} title="Delete ElectricalSystem?" message="Are you sure you want to delete this electricalSystem?"
        onConfirm={confirmDelete} onCancel={cancelDelete} confirmLabel="OK" cancelLabel="Cancel"
      />

      <SimpleFormModal
        isOpen={!!currentForm} title={currentForm ? (currentForm.mode === "edit" ? "Edit ElectricalSystem" : "Add ElectricalSystem") : ""}
        initialData={currentForm ? currentForm.data : null} fields={electricalSystemFields()} onSave={handleSaveForm} onCancel={handleCancelFormModal}
        defaultValues={electricalSystemDefaultValues}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}
    </>
  );
};

export default withAuthenticationRequired(ElectricalSystemsComponent, {
  onRedirecting: () => <Loading />,
});
